import React, { useState, useRef } from 'react'
import { Upload } from 'lucide-react'
import { projectsApi, EvidenceFile } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { canUpload as canUploadFiles } from '@/lib/roles'
import { validateUploadSize, MAX_UPLOAD_LABEL } from '@/lib/uploadLimits'

interface Props {
  profileId: string
  attachedToType: 'cbs_question' | 'vendor_response' | 'general'
  attachedToKey?: string
  // Parent receives the batch of created rows so it can append to local
  // state without refetching (which would reset scroll position).
  onUploaded: (created: EvidenceFile[]) => void
  compact?: boolean
}

export const EvidenceUploader: React.FC<Props> = ({ profileId, attachedToType, attachedToKey, onUploaded, compact }) => {
  const { error: showError } = useToast()
  const { user } = useAuth()
  const [uploading, setUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Server enforces requireAdmin on POST /system-profiles/:id/evidence; this
  // check just hides the affordance from users who'd get a 403 anyway.
  const canUpload = canUploadFiles(user)
  if (!canUpload) return null

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    const created: EvidenceFile[] = []
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const sizeError = validateUploadSize(file)
        if (sizeError) {
          showError(sizeError)
          continue
        }
        try {
          const row = await projectsApi.uploadEvidence(profileId, file, {
            attached_to_type: attachedToType,
            attached_to_key: attachedToKey,
          })
          created.push(row)
        } catch (err: any) {
          showError(err?.message ?? `Upload failed for ${file.name}`)
        }
      }
      if (created.length > 0) onUploaded(created)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <>
      <input ref={inputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.zip,application/zip,application/x-zip-compressed" className="hidden"
             onChange={e => handleFiles(e.target.files)} />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        title={`Max ${MAX_UPLOAD_LABEL} per file`}
        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-dashed ${
          uploading ? 'text-slate-300' : 'text-slate-500 hover:text-blue-600 hover:border-blue-400 dark:text-slate-400'
        } border-slate-300 dark:border-slate-600`}
      >
        <Upload className="h-2.5 w-2.5" />
        {uploading ? 'Uploading...' : compact ? 'Attach' : 'Attach evidence'}
      </button>
    </>
  )
}
